import React from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Banknote, Check, MoreVertical, Pencil, Trash2, Undo2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { costInvoiceStatusVariant } from '@/lib/status-variants'
import type { InvoiceAffordances } from '../lib/invoices'
import { INVOICE_STATUS_KEY } from '../lib/labels'
import type { CostInvoice } from '../types'

interface Props {
  invoice: CostInvoice
  af: InvoiceAffordances
  onBack: () => void
  onEdit: () => void
  onApprove: () => void
  onMarkPaid: () => void
  onBackToPending: () => void
  onBackToApproved: () => void
  onDelete: () => void
}

const InvoiceHeader: React.FC<Props> = ({ invoice, af, onBack, onEdit, onApprove, onMarkPaid, onBackToPending, onBackToApproved, onDelete }) => {
  const { t } = useTranslation()
  return (
    <div className="flex flex-wrap items-start gap-3">
      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onBack} aria-label={t('CostControl_invoice_back')}>
        <ArrowLeft className="h-4 w-4" />
      </Button>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="truncate text-xl font-semibold">{t('CostControl_invoice_title', { number: invoice.number })}</h1>
          <Badge variant={costInvoiceStatusVariant(invoice.status)}>{t(INVOICE_STATUS_KEY[invoice.status])}</Badge>
        </div>
        <p className="text-sm text-muted-foreground">{invoice.counterpartyName} · {invoice.contractName}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {af.approve && (
          <Button size="sm" onClick={onApprove}>
            <Check className="h-4 w-4" />
            {t('CostControl_invoice_approve')}
          </Button>
        )}
        {af.markPaid && (
          <Button size="sm" onClick={onMarkPaid}>
            <Banknote className="h-4 w-4" />
            {t('CostControl_invoice_mark_paid')}
          </Button>
        )}
        {af.backToPending && (
          <Button variant="outline" size="sm" onClick={onBackToPending}>
            <Undo2 className="h-4 w-4" />
            {t('CostControl_invoice_back_to_pending')}
          </Button>
        )}
        {af.backToApproved && (
          <Button variant="outline" size="sm" onClick={onBackToApproved}>
            <Undo2 className="h-4 w-4" />
            {t('CostControl_invoice_back_to_approved')}
          </Button>
        )}
        {af.editHeader && (
          <Button variant="outline" size="sm" onClick={onEdit}>
            <Pencil className="h-4 w-4" />
            {t('Common_edit')}
          </Button>
        )}
        {af.remove && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={t('CostControl_invoice_more')}>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={onDelete}>
                <Trash2 className="h-4 w-4" />
                {t('Common_delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  )
}
export default InvoiceHeader
